import * as _ from 'lodash';
import fieldUtil from './FieldNames';
import { evolve } from '../../utility/evolve';

const { accumSubKeyFields, arrayToCsv, twoSeventyOneKeys, paymentMethodFields,
  costRulesFields, groupMappingFields, getSpecialityAccums } = fieldUtil;

const stubPaymentMethods = () => ({
  ..._.fromPairs(paymentMethodFields.map(key => [key, ''])),
  default: []
});

const stubCostRules = () => ({
  ..._.fromPairs(costRulesFields.map(key => [key, ''])),
  payment_methods: stubPaymentMethods()
});

const stubGroup = () => (
  _.fromPairs(groupMappingFields.map(key => [key, '']))
);

/**
 * Given a payer config, returns the accums that are present in it
 */
const accumsForConfig = (config, specialty) => _.chain(config)
  .thru(v => _.get(v, 'two_seventy_one', {}))
  .pick(specialty)
  .values()
  .map(v => _.keys(v))
  .flatten()
  // ensure standard accums always present
  .concat(getSpecialityAccums(specialty))
  .uniq()
  .sort()
  .value();

const stubAccumNode = () => ({
  eb_fields: _.fromPairs(accumSubKeyFields.eb_fields.map(key => [key, ''])),
  messages: _.fromPairs(accumSubKeyFields.messages.map(key => [key, ''])),
  custom_filters: ['']
});
const stubSpecialty = ({ config, specialty }) => _.fromPairs(
  accumsForConfig(config, specialty).map(accum => [accum, [stubAccumNode()]])
);
const stubTwoSeventyOne = config => _.fromPairs(
  twoSeventyOneKeys.map(specialty => [specialty, stubSpecialty({ config, specialty })])
);
/**
 * Creates a stub/empty payer config, with all specialties and accums, suitable
 * for editing via Formik. Optionally passing an existing config will ensure
 * that stubbed specialties have all the accum nodes (e.g. Aetna has non
 * standard accums like `starting_deductible`).
 */
const stubConfig  = (existingConfig = null) => ({
  two_seventy_one: stubTwoSeventyOne(existingConfig),
  cost_rules:      stubCostRules(),
  group:           {}
});

const convertAccumNode = _.flow(
  node => _.castArray(node),
  nodes => _.map(nodes, evolve({
    eb_fields: obj => _.mapValues(obj, arrayToCsv),
    messages: obj => _.mapValues(obj, arrayToCsv),
    custom_filters: obj => _.castArray(obj)
  })),
  nodes => _.map(nodes, node => _.defaultsDeep(node, stubAccumNode())),
  nodes => _.map(nodes, evolve({
    eb_fields: fields => _.mapValues(fields, v => _.defaultTo(v, ''))
  }))
);
const convertSpecialty = _.flow(
  specialty => _.mapValues(specialty, convertAccumNode),
  specialty => _.defaultsDeep(specialty, stubSpecialty({ specialty }))
);

const convertPaymentMethods = methods => _.mapValues(methods, arrayToCsv);

const convertCostRules = evolve({
  deductible_waived: arrayToCsv,
  copay_only_fee_for_service: arrayToCsv,
  charge_full_when_missing_ebs: arrayToCsv,
  copay_counts_towards_oop: arrayToCsv,
  payment_methods: convertPaymentMethods
});

const toEditableFormat = _.flow(
  evolve({
    two_seventy_one: specialty => _.mapValues(specialty, convertSpecialty),
    cost_rules: convertCostRules
  }),
  config => _.defaultsDeep(config, stubConfig(config))
);

export {
  accumsForConfig,
  toEditableFormat,
  convertAccumNode,
  convertSpecialty,
  stubAccumNode,
  stubSpecialty,
  stubTwoSeventyOne,
  stubConfig,
  stubPaymentMethods,
  stubCostRules,
  stubGroup
};
